import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { Breadcrumb } from '../components/Breadcrumb';
import { PageTitle } from '../components/PageTitle';


const PAGETITLE_JP = '企業情報';
const PAGETITLE_EN = 'Company';
const PAGETITLE_MAP_JP = '本社アクセス';
const PAGETITLE_MAP_EN = 'Access';
/**
 * CompanyInfoPage Component - Company Information Page
 * 
 * Features:
 * - Company overview and details
 * - Office access information
 * - Company images and location details
 * - Mobile responsive design
 * - Auto-scroll to top on page load
 */

const CompanyInfoPageContainer = styled.div.attrs({className: 'page-container'})`
  background: #F9FAFA;
  min-height: 100vh;
  padding: 0 0 160px;

  @media (max-width: 768px) {
      padding: 0 20px 95px;
`;

const BreadcrumbSection = styled.div.attrs({className: 'breadcrumb-section'})`
  padding: 80px 0 40px;
`;

const BreadcrumbContainer = styled.div.attrs({className: 'breadcrumb-container'})`
  margin: 0 116px;
  @media (max-width: 768px) {
      margin: 0;
    }
`;

const BreadcrumbText = styled.p.attrs({className: 'breadcrumb-text'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1;
  color: #9B9B9B;
  margin: 0;

  a {
    color: #9B9B9B;
    text-decoration: none;
    transition: color 0.3s ease;

    &:hover {
      color: #0F3F8E;
    }
  }
`;



const CompanyInfoSection = styled.section.attrs({className: 'company-info-section'})`
  padding: 80px 0 240px;
  max-width: var(--contents-maxwidth-pc);
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 80px 0 120px;
  }
`;

const CompanyInfoGrid = styled.div.attrs({className: 'company-info-grid'})`
  display: grid;
  grid-template-columns: 1fr 1.3fr;
  gap: 100px;
  align-items: start;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 60px;
  }
`;

const CompanyInfoContent = styled.div.attrs({className: 'company-info-content'})`
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: 768px) {
    gap: 40px;
  }
`;

const CompanyInfoItem = styled.div.attrs({className: 'company-info-item'})`
  display: flex;
  gap: 60px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const CompanyInfoLabel = styled.div.attrs({className: 'company-info-label'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  min-width: 120px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    min-width: auto;
    font-size: 14px;
    &::before { content: "｜"; }
  }
`;

const CompanyInfoValue = styled.div.attrs({className: 'company-info-value'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;
  flex: 1;

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;
  }
`;

const CompanyImage = styled.img.attrs({className: 'company-image'})`
  width: 545px;
  height: 637px;
  border-radius: 20px;
  object-fit: cover;
  object-position: center;
  position: sticky;
  top: 160px;

  @media (max-width: 768px) {
    width: 335px;
    height: 335px;
    border-radius: 15px;
    position: relative;
    justify-self: center;
    top: 0;
  }
`;

const OfficeAccessSection = styled.section.attrs({className: 'office-access-section'})`

`;



const OfficeAccessContent = styled.div.attrs({className: 'office-access-content'})`
  display: flex;
  flex-direction: column;
  gap: 60px;
  align-items: flex-start;
  max-width: var(--contents-maxwidth-pc);
  margin: 100px auto 0;

  @media (max-width: 768px) {
    margin-top: 80px;
  }
`;

const OfficeAccessText = styled.div.attrs({className: 'office-access-text'})`
  font-family: 'Noto Sans JP', sans-serif;
  font-weight: 500;
  font-size: 16px;
  line-height: 1.75;
  color: #333333;

  p {
    margin: 0;
  }

  p:first-child {
    margin-bottom: 30px;
  }

  p:last-child {
    margin-bottom: 0;
  }

  @media (max-width: 768px) {
    font-size: 14px;
    line-height: 1.6;

    p {
      margin: 0 0 15px 0;
    }
  }
`;

const OfficeAccessMap = styled.div.attrs({className: 'office-access-map'})`
  width: 100%;
  height: auto;
  aspect-ratio: 1/1;
  border-radius: 20px;
  margin-left: -20px;
  margin-right: -20px;
  width: calc(100% + 40px);
  iframe{
    width: 100%;
    height: 100%;
    border-radius: 20px;
  }

  @media (min-width: 769px) {
    aspect-ratio: 1280/637;
    margin-right: 0;
    margin-left: 0;
    width: 100%;
  }
`;

const CompanyInfoPage: React.FC = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <CompanyInfoPageContainer>
      {/* Breadcrumb Navigation */}
      <Breadcrumb 
        items={[
          { label: PAGETITLE_JP } // 現在のページ
        ]} 
      />


      {/* Page Title Section */}
      <PageTitle title={ PAGETITLE_JP } subtitle={ PAGETITLE_EN } />

      {/* Company Information Section */}
      <CompanyInfoSection>
        <CompanyInfoGrid>
          <CompanyImage src="/assets/images/company/img_office.jpg" alt="Company Office" />
          <CompanyInfoContent>
            <CompanyInfoItem>
              <CompanyInfoLabel>会社名</CompanyInfoLabel>
              <CompanyInfoValue>株式会社リブゲート</CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>設年月日</CompanyInfoLabel>
              <CompanyInfoValue>2010年9月28日</CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>本社所在地</CompanyInfoLabel>
              <CompanyInfoValue>〒104-0032 東京都中央区八丁堀4-13-1　八丁堀東洋ビル3階</CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>大阪営業所</CompanyInfoLabel>
              <CompanyInfoValue>〒542-0081 大阪市中央区南船場2-10-30 Duo心斎橋 1階</CompanyInfoValue>
            </CompanyInfoItem>

            <CompanyInfoItem>
              <CompanyInfoLabel>資本金</CompanyInfoLabel>
              <CompanyInfoValue>1,000万円</CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>役員</CompanyInfoLabel>
              <CompanyInfoValue>代表取締役社長　佐藤 和良<br />取締役　　　　　嶋崎 天雄<br />執行役員　　　　谷口 一成</CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>従業員数</CompanyInfoLabel>
              <CompanyInfoValue>正社員　118名（2026年6月1日時点）</CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>主な事業内容</CompanyInfoLabel>
              <CompanyInfoValue>
                ソフトウェアやシステムインフラの設計・開発・検証・運用<br />
                AI/機械学習のシステム開発・ツール導入支援<br />
                データサイエンス・データ解析・BI<br />
                SaaSプラットフォームの導入支援<br />
                デジタルマーケティング支援<br />
                ブランディング支援、ウェブサイト・アプリ等の制作
              </CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>保有資格</CompanyInfoLabel>
              <CompanyInfoValue>
                プライバシーマーク登録番号第21000763(07)号<br />
                労働者派遣事業許可番号派13-305781<br />
                電気通信事業届出番号A-24-12608
              </CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>主要取引先</CompanyInfoLabel>
              <CompanyInfoValue>
              ソフトバンク株式会社<br />
              伊藤忠テクノソリューションズ株式会社<br />
              株式会社電通<br />
              株式会社電通デジタル<br />
              大日本印刷株式会社<br />
              株式会社バンダイナムコフィルムワークス
              </CompanyInfoValue>
            </CompanyInfoItem>
            
            <CompanyInfoItem>
              <CompanyInfoLabel>関連会社</CompanyInfoLabel>
              <CompanyInfoValue>株式会社ゼレンホールディングス</CompanyInfoValue>
            </CompanyInfoItem>
          </CompanyInfoContent>
        </CompanyInfoGrid>
      </CompanyInfoSection>

      
      {/* Office Access Section */}
      <OfficeAccessSection>
        {/* Page Title Section */}
        <PageTitle title={ PAGETITLE_MAP_JP } subtitle={ PAGETITLE_MAP_EN } />

        
        <OfficeAccessContent>
          <OfficeAccessMap>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d395.81659357801317!2d139.77875419311718!3d35.67458114373669!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f40!3m3!1m2!1s0x6018895dcf71305d%3A0xfda69136cc471326!2z5YWr5LiB5aCA5p2x6IiI44OT44Or!5e0!3m2!1sja!2sjp!4v1778219300030!5m2!1sja!2sjp"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Google Map"
            ></iframe>
          </OfficeAccessMap>
          <OfficeAccessText>
            <p>〒104-0032 東京都中央区八丁堀4-13-1　八丁堀東洋ビル3階</p>
            <p>「八丁堀」3B出口 徒歩30秒</p>
            <p>（東京メトロ日比谷線、JR京葉線）</p>
            <p>「茅場町」徒歩8分（東京メトロ東西線）</p>
          </OfficeAccessText>
        </OfficeAccessContent>
      </OfficeAccessSection>
    </CompanyInfoPageContainer>
  );
};

export default CompanyInfoPage;
